import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { apiErrorReason } from "@/lib/http.ts";
import { useWhatsappGroupPicker } from "@/shared/useWhatsappGroupPicker.ts";
import {
  MAX_WHATSAPP_GROUP_ADDS,
  type WhatsappGroupPickerRefusalReason,
} from "@/types/whatsappGroups.ts";
import * as Sentry from "@sentry/react";
import { WhatsappGroupChecklist } from "./WhatsappGroupChecklist.tsx";

type Props = {
  /** Runs once the join is queued, with how many groups it will add. */
  onJoined?: (queued: number) => void;
  /** Shows a "Not now" button beside Add. */
  onCancel?: () => void;
};

const NOT_LIVE: WhatsappGroupPickerRefusalReason = "notLive";
const NOT_LIVE_MESSAGE = "WhatsApp is still connecting. Try again in a moment.";

/** "1 group", "3 groups". */
function groupCount(count: number): string {
  return `${count} ${count === 1 ? "group" : "groups"}`;
}

/**
 * The group list and its "Add okthx" button, for the post-link modal. Once the
 * join is queued the button gives way to a line saying so, so the same pick
 * can't be sent twice.
 */
export const WhatsappGroupPickerForm = ({ onJoined, onCancel }: Props) => {
  const { query, join, selectedCount, groups, isSelected, isCapped, toggle, overCap } =
    useWhatsappGroupPicker({
      onJoined: ({ queued }) => {
        confirmationToast(
          queued > 0 ? `Adding okthx to ${groupCount(queued)}` : "okthx is already in those groups",
        );
        onJoined?.(queued);
      },
      onJoinError: (error) => {
        if (apiErrorReason(error) === NOT_LIVE) {
          errorToast(NOT_LIVE_MESSAGE);
          return;
        }
        Sentry.captureException(error);
        errorToast("Couldn't add okthx to your groups. Please try again.");
      },
    });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="text-sm text-muted-foreground">
        {apiErrorReason(query.error) === NOT_LIVE
          ? NOT_LIVE_MESSAGE
          : "Couldn't load your WhatsApp groups. Try again in a moment."}
      </p>
    );
  }
  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">You aren't an admin of any WhatsApp groups.</p>
    );
  }

  const addLabel = selectedCount === 0 ? "Add okthx" : `Add okthx to ${groupCount(selectedCount)}`;
  const queued = join.data?.queued ?? 0;
  const joinedMessage =
    queued > 0
      ? `okthx is joining ${groupCount(queued)}. It can take a few minutes.`
      : "okthx is already in those groups.";

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-md border">
        <OmniScrollArea className="max-h-[50vh]">
          <WhatsappGroupChecklist
            groups={groups}
            isSelected={isSelected}
            isCapped={isCapped}
            onToggle={toggle}
            disabled={join.isPending || join.isSuccess}
          />
        </OmniScrollArea>
      </div>
      {overCap && !join.isSuccess && (
        <p className="text-xs text-muted-foreground">
          Up to {MAX_WHATSAPP_GROUP_ADDS} groups at a time.
        </p>
      )}
      {join.isSuccess ? (
        <p className="text-sm text-muted-foreground">{joinedMessage}</p>
      ) : (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={join.isPending}
              data-statsig-name="whatsapp-group-picker-skip"
            >
              Not now
            </Button>
          )}
          <Button
            onClick={() => join.mutate()}
            disabled={selectedCount === 0 || join.isPending}
            data-statsig-name="whatsapp-group-picker-add"
          >
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
